import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { I18nTranslator } from '../../../../i18n/translate';
import {
  ARConsoleFormatType,
  ARConsoleType,
  ARConsoleVersion,
} from '../../../../models/agrorendement-common-lists.model';
import { GCFarm, GCOrderItemConsole, GCParcel, GCParcelGroup } from '../../../../models/agrorendement-genconsole.model';
import { ARCommonListsService } from '../../../../services/agrorendement-common-lists.service';
import { ModalService } from '../../../../services/modal.service';
import { ARGenConsoleService } from '../../../../services/agrorendement-genconsole.service';
import { isGenConsoleFormatSupported, isGenConsoleModelSupported, isGenConsoleVersionSupported } from './utils';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { FormsModule } from '@angular/forms';

@Component({
  standalone: true,
  selector: 'ar-genconsole-groupparcels-cmp',
  templateUrl: './genconsole.groupparcels.component.html',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, CommonModule, TranslatePipe, FormsModule],
  providers: [ARGenConsoleService, ARCommonListsService],
})
export class ARGenConsoleGroupParcelsComponent implements OnInit, AfterViewInit {
  @Input()
  culture: string;

  @Input()
  farms: Array<GCFarm>;

  @Input()
  parcels: Array<GCParcel>;

  @Input()
  groups: Array<GCParcelGroup>;

  model: Array<Object>;

  groupNames: Array<string>;

  constructor(
    private genconsoleService: ARGenConsoleService,
    private commonListService: ARCommonListsService,
    private i18nTranslator: I18nTranslator,
    private modal: ModalService,
    private router: Router,
  ) {}

  ngOnInit() {
    if (!this.groups || this.groups.length == 0) {
      const groups = new Array<GCParcelGroup>();
      for (const parcel of this.parcels) {
        const pg = new GCParcelGroup();
        pg.parcelId = parcel.id;
        pg.name = parcel.name;
        pg.groupName = null;
        groups.push(pg);

        parcel.group = pg;
      }

      this.groups.push(...groups);
    }

    if (this.groups.length <= 26) {
      this.groupNames = this.permutations(1);
    } else {
      // devrait etre suffisant
      this.groupNames = this.permutations(1);
      this.groupNames.push(...this.permutations(2));
    }

    let currentChoiceIndex = 0;
    this.model = new Array<Object>();
    for (const farm of this.farms) {
      const fertConcentrationIndex = new Map<string, Array<GCParcel>>();
      for (const parcel of farm.parcels) {
        const console = parcel.console;
        // TODO normer la concentration ? champ libre
        const consoleKey =
          console.consoleType.code + '_' + console.consoleVersion.code + '_' + console.consoleFormatType.code;
        const fetConcentrationKey = console.fertilizerType.code + '_' + console.fertilizerConcentration;
        const fcParcelKey = consoleKey + fetConcentrationKey;
        let fcParcels = fertConcentrationIndex.get(fcParcelKey);
        if (!fcParcels) {
          fcParcels = new Array<GCParcel>();
          fertConcentrationIndex.set(fcParcelKey, fcParcels);
        }
        fcParcels.push(parcel);
      }

      const obj: any = {};
      obj['farm'] = farm;
      const fertGroupings: any = [];
      obj['fertGroupings'] = fertGroupings;
      fertConcentrationIndex.forEach((fcParcels, key) => {
        // console + concentration + ftype identique pour toutes les fcParcels
        const console = fcParcels[0].console;
        const consoleType = console.consoleType;
        const consoleVersion = console.consoleVersion;
        const consoleFormat = console.consoleFormatType;
        const fConcentration = console.fertilizerConcentration;
        const fType = console.fertilizerType;
        let fLabel: string = null;
        // au lieu d'aller faire une requete
        // TODO faire une requete ?
        if (fType.code == 'SOLID') {
          fLabel = this.i18nTranslator.translate('ar.gc.gp.solid');
        } else {
          fLabel = this.i18nTranslator.translate('ar.gc.gp.liquid');
        }

        const label =
          this.labelConsoleType(consoleType) +
          ', ' +
          this.labelVersion(consoleVersion) +
          ' (' +
          this.labelFormatType(consoleFormat) +
          ') - ' +
          fLabel +
          ' ' +
          fConcentration +
          '%';

        // suppose que groupNames est suffisamment grand
        // choix differents par grouping FertType+concentration
        const untilChoiceIndex = currentChoiceIndex + fcParcels.length;
        const groupChoices = this.groupNames.slice(currentChoiceIndex, untilChoiceIndex);
        currentChoiceIndex = untilChoiceIndex;

        fertGroupings.push({ label: label, parcels: fcParcels, choices: groupChoices, console: console });
      });
      this.model.push(obj);
    }
  }

  ngAfterViewInit() {}

  clear() {
    this.groups.splice(0, this.groups.length);
  }

  permutations(level: number): Array<string> {
    const texts0 = new Array<string>();
    for (let i = 0; i < 26; i++) {
      const text = String.fromCharCode(97 + i).toUpperCase();
      texts0.push(text);
    }

    if (level == 1) {
      return texts0;
    }

    const subTexts = this.permutations(level - 1);
    const texts = new Array<string>();
    for (const text of texts0) {
      for (const subText of subTexts) {
        texts.push(text + subText);
      }
    }

    return texts;
  }

  labelFormatType(format: ARConsoleFormatType) {
    if (format && format.label) {
      return format.label;
    }

    if (format && format.code == 'UNKNOWN') {
      return this.i18nTranslator.translate('ar.gc.sc.unknown');
    }
    return '';
  }

  labelConsoleType(consoleType: ARConsoleType) {
    if (consoleType && consoleType.label) {
      return consoleType.label;
    }

    if (consoleType && consoleType.code === 'OTHER') {
      return this.i18nTranslator.translate('ar.gc.sc.other.marque');
    }

    return '';
  }

  labelVersion(version: ARConsoleVersion) {
    if (version && version.label) {
      return version.label;
    }

    if (version && version.code === 'OTHER') {
      return this.i18nTranslator.translate('ar.gc.sc.other.version');
    }

    return '';
  }

  isFormatUnsupported(console: GCOrderItemConsole) {
    return !isGenConsoleFormatSupported(console.consoleFormatType);
  }

  isConsoleUnsupported(console: GCOrderItemConsole) {
    return !isGenConsoleModelSupported(console.consoleType);
  }

  isVersionUnsupported(console: GCOrderItemConsole) {
    return !isGenConsoleVersionSupported(console.consoleVersion);
  }
}
